import { Alert, AlertColor, AlertTitle, Collapse, Stack, SxProps, Theme } from '@mui/material';
import { ReactNode, useState } from 'react';
import { AlertBannerAction } from './alert-banner-action';
import { theme } from '../../..';
import { Close, DangerousOutlined, PendingOutlined, SvgIconComponent } from '@mui/icons-material';

export interface AlertBannerProps {
  title?: string;
  severity: AlertColor;
  children?: ReactNode;
  sx?: SxProps<Theme>;
  onDismiss?: () => void;
  onAction?: () => void;
  actionIcon?: SvgIconComponent;
  actionDescription?: string;
  disabled?: boolean;
}

export const AlertBanner = ({
  title,
  severity,
  children,
  disabled,
  onDismiss,
  onAction,
  actionIcon,
  actionDescription,
  sx,
}: AlertBannerProps) => {
  const [open, setOpen] = useState(true);

  const handleDismiss = () => {
    setOpen(false);
    onDismiss?.();
  };

  const shouldShowActions = onAction || onDismiss;
  const borderColor = disabled ? '#00000042' : `${severity}.accent`;
  const backgroundColor = disabled ? '#00000001' : `${severity}.light`;

  const errorIconOverride = severity === 'error' ? <DangerousOutlined /> : undefined;

  const icon = disabled ? <PendingOutlined color="disabled" /> : errorIconOverride;

  // Compose action and close button if both are provided
  const Actions = (
    // If using a title, position the buttons in the top right corner, otherwise align with the message
    <Stack direction="row" sx={title ? { position: 'absolute', top: 10, right: 10 } : {}}>
      {onAction && (
        <AlertBannerAction
          data-testid="action"
          onClick={onAction}
          actionIcon={actionIcon}
          tooltip={actionDescription}
        />
      )}

      {onDismiss && (
        <AlertBannerAction
          data-testid="close"
          actionIcon={Close}
          onClick={handleDismiss}
          tooltip="Dismiss"
        />
      )}
    </Stack>
  );

  return (
    <Collapse in={open} sx={{ width: '100%' }}>
      <Alert
        severity={severity}
        action={shouldShowActions ? Actions : undefined}
        variant="outlined"
        icon={icon}
        sx={{
          position: 'relative',
          '& .MuiAlert-message': { width: '100%' },
          color: `inherit`,
          borderColor,
          backgroundColor,
          ...sx,
        }}
      >
        {title && (
          <AlertTitle sx={{ color: disabled ? theme.palette.text.disabled : `${severity}.main` }}>
            {title}
          </AlertTitle>
        )}
        <div style={{ color: disabled ? theme.palette.text.disabled : undefined }}>{children}</div>
      </Alert>
    </Collapse>
  );
};
